import store from '@/store';
import axios from 'axios';

const baseURL = `${process.env.VUE_APP_API_URL}`;
// const getAuthHeader = (token) => ({ Authorization: `Bearer ${token}` });
const getAuthHeader = (token) => {
    const devMode = store.state.user.devMode;

    if (devMode) {
        return { 'X-API-KEY': `${token}` };
    } else {
        return { Authorization: `Bearer ${token}` };
    }
};

const apiRequest = async (method, url, data = null, params = {}, cancelToken = null) => {
    try {
        return await store.dispatch('user/apiCall', async (token) => {
            const config = {
                method,
                url: `${baseURL}/${url}`,
                headers: getAuthHeader(token),
                params,
                data,
                ...(cancelToken ? { cancelToken: cancelToken } : {}),
            };
            return await axios(config);
        });
    } catch (error) {
        if (!axios.isCancel(error)) {
            console.error('API request failed', error);
        }
        throw error;
    }
};

const SkillsService = {
    getSkill(skillId) {
        return apiRequest('get', `marketplace/skills/${skillId}`);
    },

    getSkills(page = 0, size = 50, includePrivate = false) {
        const params = { page, size };
        if (includePrivate) params.includePrivate = true;

        return apiRequest('get', 'marketplace/skills', null, params);
    },

    /**
     * El marketplace entero, recorriendo todas las páginas. Pedir solo la primera
     * cortaba en silencio en cuanto hubiera más skills que el tamaño de página.
     *
     * @param {Object} [options]
     * @param {boolean} [options.includePrivate] - También las no públicas. Solo staff de
     *        Lixsa: el backend responde 4xx a cualquier otro.
     * @returns {Promise<Object[]>} Skills tal cual las devuelve el backend.
     */
    async getAllSkills({ includePrivate = false } = {}) {
        const size = 100;
        const maxPages = 20; // tope de seguridad: 2.000 skills
        const skills = [];
        for (let page = 0; page < maxPages; page++) {
            const response = await this.getSkills(page, size, includePrivate);
            const data = response?.data || {};
            skills.push(...(data.content || []));
            if (data.last !== false || !(data.content || []).length) break;
        }
        return skills;
    },

    /**
     * Lo que se enseña en las tiendas (integraciones y skills): las públicas para
     * todos y, para el staff de Lixsa, también las no públicas (`skill.public === false`),
     * que las tarjetas marcan como privadas.
     *
     * Si la petición con privadas falla (backend sin desplegar, permiso denegado),
     * el staff ve al menos lo mismo que un cliente en vez de una tienda vacía.
     *
     * @param {Object} [options]
     * @param {boolean} [options.isStaff] - getter `user/isWalkonLivosAccount`.
     * @returns {Promise<Object[]>}
     */
    async getMarketplaceSkills({ isStaff = false } = {}) {
        if (isStaff) {
            try {
                return await this.getAllSkills({ includePrivate: true });
            } catch (error) {
                console.warn('Could not load private skills, falling back to public ones', error);
            }
        }
        const skills = await this.getAllSkills();
        return skills.filter((skill) => skill.public === true);
    },

    searchSkills(searchTerm = null, category = null, page = 0, size = 20) {
        const params = {
            page,
            size,
        };

        if (searchTerm) params.search = searchTerm;
        if (category) params.category = category;

        return apiRequest('get', 'marketplace/skills/search', null, params).catch((thrown) => {
            if (!axios.isCancel(thrown)) {
                throw thrown;
            }
        });
    },
};

export default SkillsService;
